import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[38px] text-base md:text-base font-medium md:font-normal transition-all duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-primary/90",
        ink: "bg-ink text-paper hover:bg-ink/90 shadow-[0_10px_30px_rgba(24,24,24,0.08)]",
        secondary: "bg-grey-200 text-dark hover:brightness-95",
        muted:
          "border border-line-strong bg-surface text-muted hover:bg-surface-hover",
        light: "bg-sand text-dark hover:text-sand hover:bg-primary",
        // link: "text-primary underline-offset-4 hover:underline",
        destructive: "bg-red-100 text-red-800 hover:brightness-95",
        gradientLight: "bg-linear-gradient-light text-white",
        white:
          "w-full shadow-[0_0.0625rem_0.125rem_0_rgba(0,0,0,0.03)] hover:brightness-95 transition-all bg-white text-ink font-medium border border-grey-stroke",
      },
      size: {
        default: "px-[18px] py-[16px]",
        md: "md:py-4.5 py-3 px-2 rounded-[0.625rem] text-lg leading-[1.625rem]",
        sm: "px-[24px] py-[12px] rounded-[30px] text-xs md:text-base",
        chip: "h-10 rounded-full px-4 py-2 text-[14px] font-normal md:text-[15px]",
        checkout:
          "h-[64px] w-full rounded-full px-8 text-[18px] font-normal md:h-[72px] md:text-[20px]",
        // sm: "h-8 rounded-md px-3 text-xs",
        // lg: "h-10 rounded-md px-8",
        // icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  onHover?: boolean;
  hoverGradientLight?: boolean;
  hoverDark?: boolean;
  hoverLight?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      onHover = false,
      hoverGradientLight = false,
      hoverDark = false,
      hoverLight = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const hoverMotionClasses = onHover
      ? "[filter:brightness(1)] transform-gpu hover:[filter:brightness(1.1)] active:scale-95"
      : "";

    const hoverGradientLightClasses = hoverGradientLight
      ? "relative isolate hover:text-white before:pointer-events-none before:content-[''] before:absolute before:-inset-[1px] before:rounded-[40px] before:opacity-0 before:transition-opacity before:duration-300 before:ease-out before:[background-image:var(--gradient-brand-light)] before:z-0 hover:before:opacity-100"
      : "";

    const hoverDarkClasses = hoverDark ? "hover:bg-dark hover:text-white" : "";

    const hoverLightClasses = hoverLight
      ? "border border-transparent hover:bg-white hover:text-ink hover:border-ink"
      : "";

    const childrenWithZIndex = asChild
      ? props.children
      : React.Children.map(props.children, (child) => {
          if (typeof child === "string" || typeof child === "number") {
            return <span className="relative z-10">{child}</span>;
          }
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              className: cn((child.props as any).className, "relative z-10"),
            } as any);
          }
          return child;
        });

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          hoverMotionClasses,
          hoverGradientLightClasses,
          hoverDarkClasses,
          hoverLightClasses,
        )}
        ref={ref}
        {...props}
      >
        {childrenWithZIndex}
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
